"use client";

import clsx from "clsx";
import { ChevronsDown } from "lucide-react";
import React, { useState } from "react";
import { Box } from "../ui/box";
import { Button } from "../ui/button";

interface LoadMoreProps<T> {
  items: T[];
  renderItems: (item: T, index: number) => React.ReactNode;
  initialVisibleItems?: number;
  buttonText?: string;
  className?: string;
  overlayHidden?: boolean;
  noOverflow?: boolean;
  wrapper?: React.ReactElement<Record<string, unknown>, string | React.JSXElementConstructor<any>>;
}

export const LoadMore = <T,>({
  items,
  renderItems,
  initialVisibleItems = 6,
  buttonText,
  className,
  wrapper,
  overlayHidden = false,
  noOverflow = false,
}: LoadMoreProps<T>): React.ReactElement => {
  const [expanded, setExpanded] = useState(false);

  const itemsToShow = expanded ? items : items.slice(0, initialVisibleItems);

  return (
    <Box className="relative">
      <Box
        className={clsx(className, {
          "overflow-hidden p-px": noOverflow
            ? false
            : !expanded && items.length > initialVisibleItems,
          "pb-4": overlayHidden,
        })}
        render={wrapper ?? <div />}
      >
        {itemsToShow.map((item, index) => renderItems(item, index))}
      </Box>
      {!overlayHidden && (
        <Box
          className={clsx(
            "from-background absolute right-0 bottom-10 left-0 h-16 bg-linear-to-t to-transparent transition-opacity duration-500",
            {
              "pointer-events-none opacity-0": expanded || items.length <= initialVisibleItems,
              "pointer-events-auto opacity-100": !expanded && items.length > initialVisibleItems,
            }
          )}
        />
      )}

      {!expanded && items.length > initialVisibleItems && (
        <div className="bg-common-white text-center">
          <Button onClick={() => setExpanded(true)} variant={"link"}>
            {buttonText || "View More"} <ChevronsDown size={20} />
          </Button>
        </div>
      )}
    </Box>
  );
};
